import { EventEmitter } from "events";
import { CodedError } from "./CodedError";
class StorageHandler extends EventEmitter {
    namespace;
    version;
    cacheValidated = false;
    handlers;
    invalidatedCb;
    static namespaces = {};
    constructor(namespace, version, options) {
        super();
        const { invalidated = () => null, validateOnInit = true, } = options;
        this.namespace = namespace;
        this.version = version;
        this.handlers = {};
        this.invalidatedCb = invalidated;
        this.getCacheKey = this.getCacheKey.bind(this);
        this.getCacheVersionKey = this.getCacheVersionKey.bind(this);
        this.invalidateCache = this.invalidateCache.bind(this);
        this.checkCache = this.checkCache.bind(this);
        this.getKeyHandler = this.getKeyHandler.bind(this);
        if (validateOnInit)
            this.checkCache();
    }
    static init(namespace, version, options = {}) {
        const ns = StorageHandler.namespaces[namespace] = (StorageHandler.namespaces[namespace] || {});
        const instance = ns[version] = (ns[version] || new StorageHandler(namespace, version, options));
        return instance;
    }
    getCacheKey(key = "") { return [this.namespace, key].filter(Boolean).join("_"); }
    getCacheVersionKey() { return this.getCacheKey("version"); }
    invalidateCache() {
        if (typeof localStorage === "undefined")
            return;
        const keyPrefix = this.getCacheKey();
        let invalidated = false;
        for (const cacheKey of Object.keys(localStorage)) {
            if (cacheKey.includes(keyPrefix)) {
                localStorage.removeItem(cacheKey);
                invalidated = true;
            }
        }
        localStorage.setItem(this.getCacheVersionKey(), this.version);
        if (invalidated) {
            this.handlers = {};
            this.emit("invalidated");
            this.invalidatedCb();
        }
    }
    checkCache() {
        if (this.cacheValidated ||
            typeof localStorage === "undefined")
            return;
        const cacheVersion = localStorage.getItem(this.getCacheVersionKey());
        if (!cacheVersion || cacheVersion !== this.version) {
            console.warn("cached version not found OR version mismatch...");
            this.invalidateCache();
            console.log("cache invalidated... ");
        }
        this.cacheValidated = true;
    }
    getKeyHandler(key, defaultValue, validate) {
        key = this.getCacheKey(key);
        if (this.handlers[key])
            return this.handlers[key];
        const getStateValue = (val) => {
            if (typeof defaultValue === "function") {
                return defaultValue(val);
            }
            return val || defaultValue;
        };
        let valType = typeof getStateValue();
        let handlerInitialization = true;
        const handler = {
            get: () => {
                if (typeof localStorage === "undefined")
                    return getStateValue();
                let ret = getStateValue();
                try {
                    const item = localStorage.getItem(key);
                    if (item)
                        ret = getStateValue(JSON.parse(item));
                    if (validate && typeof validate === "function") {
                        const isValid = validate(ret);
                        if (!isValid) {
                            console.warn([
                                `cache value for ${key} is invalid`,
                                "resetting it to default",
                            ].join("\n"));
                            ret = getStateValue();
                        }
                    }
                    valType = typeof ret;
                }
                catch (e) {
                    console.error(e.message + "");
                    console.warn([
                        `Failed to parse cached value for ${key}... `,
                        `Invalidating cache... `,
                    ].join("\n"));
                    this.invalidateCache();
                }
                return ret;
            },
            set: (newValue) => {
                if (typeof newValue !== valType) {
                    console.warn([
                        `The value trying to be set for key: '${key}' is '${newValue}'.`,
                        `Expecting type: ${valType}`,
                        `Invalidating cache...`,
                    ].join("\n"));
                    this.invalidateCache();
                    if (handlerInitialization) {
                        newValue = getStateValue();
                    }
                    else {
                        const err = new CodedError("Cache Invalidated", "CACHE_INVAL");
                        throw err;
                    }
                }
                if (typeof localStorage !== "undefined") {
                    localStorage.setItem(key, JSON.stringify(newValue));
                }
            },
        };
        handler.set(handler.get());
        handlerInitialization = false;
        this.handlers[key] = handler;
        return handler;
    }
}
export const initStorageNamespace = StorageHandler.init;
//# sourceMappingURL=StorageHandler.js.map